/**
 * 机上調査点の「結果の読み取り」と「画像のアドレス」の正本 (pure、docs/27)。
 *
 * 2026-09-18: 管理画面 (apps/admin/src/features/street-survey/) にしか無かった pure な読み取り処理を
 * ここへ移した。机上調査のエクスポート (functions/src/admin/exportStreetSurvey.ts) が**画面と同じ
 * 読み方**をするため (docs/98 R-98 の教訓: 読み取りの決め打ち・二重実装が表示と出力のずれを生む)。
 * admin 側の元のモジュールは、呼出側を変えずに済むようここから re-export する。
 *
 * 含むもの:
 *   - 解析結果の読み取り (`resolveStreetSurveyGenericResult` / `toStreetSurveyPost`)
 *   - マスタ行の解決 (`buildByCategoryCdMap` / `resolveMasterRow`) と検出要素の見出し・根拠
 *   - 検出の方角・距離 (`resolvePinLocation`)
 *   - 画像のアドレス (`buildStreetSurveyImageUrl` ほか)
 */
import { normalizeAiOutputFormat, STREET_SURVEY_DESCRIPTION_KEY } from './index.js';
import { STREET_SURVEY_DIRECTION_LABELS } from './streetSurveyPipeline.js';
import { DEFAULT_STREET_SURVEY_KEY, isDefaultStreetSurvey, readStreetSurveyResult, } from './streetSurveys.js';
const EMPTY_GENERIC_RESULT = {
    classifications: [],
    description: null,
    professionalInsight: null,
};
/** `source` から `key` の値を型 `T` で取り出す (型不一致は null)。 */
function pick(source, key, guard) {
    if (!key)
        return null;
    const v = source[key];
    return guard(v) ? v : null;
}
const isString = (v) => typeof v === 'string';
/**
 * 机上調査の解析結果を、モード/テンプレート固有の決め打ちをせずに読み取る (2026-07-14)。
 *
 * `aiAnalysis.generic` (docs/105「列名動的化レベル3」) を優先し、分類配列・説明文・所見の項目は
 * 出力フォーマットの `fields[]` と設定 (記録 / テンプレート / 地区の上書き) から解決する。
 * `generic` が無い古い地点 (walker mode) は `aiAnalysis.walker` にフォールバックする。
 */
export function resolveStreetSurveyGenericResult(aiRaw, schema, options = {}) {
    const recordedItemsKey = options.itemsKey?.trim() || null;
    if (!aiRaw)
        return EMPTY_GENERIC_RESULT;
    const generic = aiRaw.generic;
    const genericSource = generic && typeof generic === 'object' && !Array.isArray(generic)
        ? generic
        : null;
    const walker = aiRaw.walker;
    const walkerSource = !genericSource && walker && typeof walker === 'object' && !Array.isArray(walker)
        ? walker
        : null;
    const source = genericSource ?? walkerSource;
    if (!source)
        return EMPTY_GENERIC_RESULT;
    const descriptionKey = options.descriptionKey?.trim() || (recordedItemsKey ? STREET_SURVEY_DESCRIPTION_KEY : null);
    const insightKey = options.insightKey?.trim() || null;
    let classificationsKey;
    if (!genericSource) {
        // legacy walker フォールバック (generic を持たない古い地点、分類セットのキーは固定名のまま)。
        classificationsKey = 'classifications';
    }
    else if (!schema) {
        classificationsKey = recordedItemsKey ?? 'classifications';
    }
    else {
        const fields = normalizeAiOutputFormat(schema).fields ?? [];
        classificationsKey =
            recordedItemsKey ?? fields.find((f) => f.kind === 'object-array')?.key ?? 'classifications';
    }
    const classificationsRaw = source[classificationsKey];
    return {
        classifications: Array.isArray(classificationsRaw)
            ? classificationsRaw
            : [],
        description: pick(source, descriptionKey, isString),
        professionalInsight: pick(source, insightKey, isString),
    };
}
/** Firestore の Timestamp / ISO 文字列 / Date を Date に寄せる (client SDK / Admin SDK 双方に対応)。 */
function toDate(value) {
    if (!value)
        return null;
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
        try {
            const d = value.toDate();
            return Number.isNaN(d.getTime()) ? null : d;
        }
        catch {
            return null;
        }
    }
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
}
/**
 * Post ドキュメント → `StreetSurveyPost` 投影 (一覧・詳細・エクスポートで共有)。
 * `schema` は地区の上書きを反映した実効スキーマ、`resultFields` は説明文・所見として表示する項目
 * (docs/27 §17-3)。`surveyKey` が追加の調査なら投稿の `streetSurveyResults.{key}` を読む。
 */
export function toStreetSurveyPost(id, data, schema, resultFields, surveyKey = DEFAULT_STREET_SURVEY_KEY) {
    const loc = data.location;
    const baseStreetView = data.streetView ?? null;
    let aiRaw;
    let streetView;
    let resultAnalyzedAt = null;
    if (isDefaultStreetSurvey(surveyKey)) {
        aiRaw = data.aiAnalysis ?? undefined;
        streetView = baseStreetView;
    }
    else {
        // 追加の調査: 地点の画像の情報 (方角・パノラマ) は共通、結果と検出の位置は調査ごと。
        const result = readStreetSurveyResult(data, surveyKey);
        aiRaw = result?.aiAnalysis;
        resultAnalyzedAt = result?.analyzedAt ?? null;
        streetView = baseStreetView
            ? {
                ...baseStreetView,
                pins: result?.pins ?? [],
                ...(result?.extraction ? { extraction: result.extraction } : { extraction: undefined }),
            }
            : null;
    }
    const resolved = resolveStreetSurveyGenericResult(aiRaw ?? null, schema, {
        itemsKey: streetView?.extraction?.itemsKey,
        descriptionKey: resultFields?.descriptionKey,
        insightKey: resultFields?.insightKey,
    });
    const photo = data.photo ?? null;
    const photoPath = typeof data.photoPath === 'string' ? data.photoPath : null;
    const provider = streetView?.provider === 'upload-360' ||
        (streetView?.provider === undefined && photo?.projection === 'equirectangular' && photoPath)
        ? 'upload-360'
        : 'google';
    const aiStatus = aiRaw?.status;
    const analysisStatus = aiStatus === 'done'
        ? 'done'
        : aiStatus === 'failed'
            ? 'failed'
            : aiStatus === 'pending' || aiStatus === 'processing'
                ? 'pending'
                : 'none';
    return {
        id,
        location: {
            latitude: typeof loc?.latitude === 'number' ? loc.latitude : 0,
            longitude: typeof loc?.longitude === 'number' ? loc.longitude : 0,
        },
        settingsId: aiRaw?.settingsId ?? null,
        description: resolved.description,
        professionalInsight: resolved.professionalInsight,
        classifications: resolved.classifications,
        streetView,
        createdAt: toDate(data.createdAt),
        provider,
        analysisStatus,
        analysisRunId: typeof aiRaw?.runId === 'string' ? aiRaw.runId : null,
        photoPath,
        photoThumbPath: typeof data.photoThumbPath === 'string' ? data.photoThumbPath : null,
        photoUrl: typeof data.photoUrl === 'string' ? data.photoUrl : null,
        photoThumbUrl: typeof data.photoThumbUrl === 'string' ? data.photoThumbUrl : null,
        photo,
        title: typeof data.title === 'string' ? data.title : '',
        fileName: typeof photo?.fileName === 'string' ? photo.fileName : null,
        capturedAt: toDate(photo?.capturedAt),
        analyzedAt: resultAnalyzedAt ?? (typeof aiRaw?.analyzedAt === 'string' ? aiRaw.analyzedAt : null),
    };
}
/* ===== マスタ行の解決 (分類コード → 行) ===== */
/**
 * マスタ行を引くためのキーを作る。キーは 3 系統:
 *   - 素の `categoryCd` (単一マスタ内で一意な場合の後方互換)
 *   - `"majorCd-categoryCd"` (大分類をまたいで番号が重複するマスタでも一意に解決する主経路)
 *   - 行の名称 `category` (値をコードではなく名称で保存する項目のため。コードのキーを上書きしない)
 */
export function buildByCategoryCdMap(rows) {
    const byCategoryCd = new Map();
    for (const r of rows ?? []) {
        if (r.categoryCd && !byCategoryCd.has(r.categoryCd))
            byCategoryCd.set(r.categoryCd, r);
        if (r.majorCd && r.categoryCd) {
            const combined = `${r.majorCd}-${r.categoryCd}`;
            if (!byCategoryCd.has(combined))
                byCategoryCd.set(combined, r);
        }
    }
    for (const r of rows ?? []) {
        const name = r.category?.trim();
        if (name && !byCategoryCd.has(name))
            byCategoryCd.set(name, r);
    }
    return byCategoryCd;
}
/**
 * AI が返す分類コードをマスタ行に解決する。完全一致 → 最後のハイフン以降を categoryCd とみなす順。
 */
export function resolveMasterRow(code, byCategoryCd) {
    const exact = byCategoryCd.get(code);
    if (exact)
        return exact;
    const idx = code.lastIndexOf('-');
    if (idx < 0)
        return undefined;
    return byCategoryCd.get(code.slice(idx + 1));
}
/**
 * 検出要素の見出し。選んだ軸の値をマスタの行で名前にする → 軸の名称の列 → 値そのもの。
 * `category_name` / `gql_name` のような特定テンプレートの列名は使わない (docs/98 R-98 中)。
 */
export function resolveClassificationTitleByAxis(classification, axis) {
    const code = String(classification[axis?.codeField ?? ''] ?? '').trim();
    const row = code && axis ? resolveMasterRow(code, axis.byCategoryCd) : null;
    const nameValue = axis?.nameField ? String(classification[axis.nameField] ?? '').trim() : '';
    return row?.category || nameValue || code || '(分類名未設定)';
}
/* ===== 検出の方角・距離・根拠 ===== */
/** ランク要素の撮影方角・推定距離を `streetView.pins[]` から解決する。 */
export function resolvePinLocation(post, rank) {
    const streetView = post.streetView;
    if (!streetView)
        return null;
    const pin = streetView.pins.find((p) => p.rank === rank);
    if (!pin)
        return null;
    return {
        directionLabel: STREET_SURVEY_DIRECTION_LABELS[pin.imageIndex] ?? '不明',
        heading: streetView.imageParams.headings[pin.imageIndex] ?? 0,
        distanceMeters: pin.distanceMeters,
    };
}
/**
 * 検出要素の根拠の列。新方式の地点は解析時に記録した列 (`extraction.reasonKey`)、
 * 無ければ実効の出力フォーマットの `reasoningField` (docs/98 R-98 中)。
 */
export function resolveReasonKey(post, reasoningField) {
    return post.streetView?.extraction?.reasonKey || reasoningField || null;
}
/** 検出要素の根拠の文字 (列が無い古い結果は `reasoning` を読む)。 */
export function resolveReasonText(post, classification, reasoningField) {
    const key = resolveReasonKey(post, reasoningField);
    const value = (key ? classification[key] : undefined) ?? classification.reasoning;
    return typeof value === 'string' ? value : value == null ? '' : String(value);
}
/* ===== 画像のアドレス ===== */
/**
 * Google ストリートビューのパノラマを開くリンク (Maps URLs API、API キー不要)。
 *
 * 机上調査の Google 由来点は **静止画を保存していない** (ToS §9-1 / REQ-SV.8: 表示のたびに
 * サーバの API キーで取り直す)。そのため配布物には画像そのものの URL ではなく、同じ場所・同じ向きの
 * パノラマを開くリンクを入れる。Street View Static API の URL は **サーバ専用の API キーが平文で
 * 載る**ため使わない (docs/25 §7-3 のキー分離方針)。
 */
export function buildStreetViewPanoUrl(streetView, heading) {
    if (!streetView)
        return null;
    const params = new URLSearchParams({ api: '1', map_action: 'pano' });
    const panoId = typeof streetView.panoId === 'string' ? streetView.panoId.trim() : '';
    if (panoId) {
        params.set('pano', panoId);
    }
    else if (typeof streetView.panoLocation?.latitude === 'number' &&
        typeof streetView.panoLocation?.longitude === 'number') {
        // panoId の無い古い地点は座標から最寄りのパノラマを開く。
        params.set('viewpoint', `${streetView.panoLocation.latitude},${streetView.panoLocation.longitude}`);
    }
    else {
        return null;
    }
    if (typeof heading === 'number' && Number.isFinite(heading)) {
        params.set('heading', String(Math.round(((heading % 360) + 360) % 360)));
    }
    const pitch = streetView.imageParams?.pitch;
    if (typeof pitch === 'number' && Number.isFinite(pitch))
        params.set('pitch', String(pitch));
    const fov = streetView.imageParams?.fov;
    if (typeof fov === 'number' && Number.isFinite(fov))
        params.set('fov', String(fov));
    return `https://www.google.com/maps/@?${params.toString()}`;
}
/**
 * 机上調査点の「画像のアドレス」。
 *   - 360度写真の地点: 全天球画像そのものの URL (`photoUrl`)。4 方向の画像はファイルとして存在せず、
 *     表示のたびに全天球から切り出しているため URL 化できるのは 1 枚だけ。
 *   - Google 由来の地点: パノラマを開くリンク (上記)。
 */
export function buildStreetSurveyImageUrl(post, heading) {
    if (post.provider === 'upload-360')
        return post.photoUrl ?? null;
    return buildStreetViewPanoUrl(post.streetView, heading ?? post.streetView?.baseHeading ?? null);
}
/** 4 方向 (前方 / 右側 / 後方 / 左側) の画像のアドレス。360度写真の地点は全天球 1 枚なので null。 */
export function buildStreetSurveyDirectionUrls(post) {
    const headings = post.streetView?.imageParams?.headings ?? [];
    return STREET_SURVEY_DIRECTION_LABELS.map((label, i) => ({
        label,
        url: post.provider === 'upload-360'
            ? null
            : buildStreetViewPanoUrl(post.streetView, headings[i] ?? null),
    }));
}
//# sourceMappingURL=streetSurveyProjection.js.map